import type { PublicTestimonial } from '../types';
import { nextTestimonialIndex, testimonialRatingLabel } from '../modules/testimonials';
import { element, registerCleanup } from './dom';

function testimonialCard(testimonial: PublicTestimonial, duplicate = false): HTMLElement {
  const card = element('article', { className: 'testimonial-card' });
  if (duplicate) card.setAttribute('aria-hidden', 'true');
  const rating = testimonialRatingLabel(testimonial.rating);
  if (rating) card.append(element('p', { className: 'testimonial-rating', text: rating, attrs: { 'aria-label': `${testimonial.rating} out of 5 stars` } }));
  card.append(element('blockquote', { text: `“${testimonial.testimonial_text}”` }));
  const identity = element('footer', { className: 'testimonial-identity' });
  identity.append(element('strong', { text: testimonial.display_name }));
  if (testimonial.verified_reader) identity.append(element('span', { className: 'testimonial-verified', text: 'Verified Reader' }));
  card.append(identity);
  return card;
}

export function testimonialCarousel(testimonials: PublicTestimonial[], cleanupRoot?: HTMLElement): HTMLElement {
  const items = testimonials.slice(0, 5);
  const section = element('section', { className: 'testimonials-section', attrs: { 'aria-labelledby': 'testimonials-title' } });
  section.append(
    element('p', { className: 'eyebrow', text: '✦ Reader Experiences ✦' }),
    element('h2', { text: 'What Readers Are Saying', attrs: { id: 'testimonials-title' } }),
    element('p', { className: 'testimonials-support', text: 'Real experiences from people who explored their Alunar reading.' }),
  );
  const carousel = element('div', { className: 'testimonial-carousel', attrs: { role: 'region', 'aria-roledescription': 'carousel', 'aria-label': 'Reader testimonials' } });
  const viewport = element('div', { className: 'testimonial-viewport', attrs: { tabindex: '0' } });
  const track = element('div', { className: 'testimonial-track' });
  if (items.length === 1) {
    track.append(testimonialCard(items[0]!));
  } else {
    track.append(testimonialCard(items.at(-1)!, true));
    items.forEach((item) => track.append(testimonialCard(item)));
    track.append(testimonialCard(items[0]!, true));
  }
  viewport.append(track);
  carousel.append(viewport);
  section.append(carousel);
  if (items.length <= 1) return section;

  const controls = element('div', { className: 'testimonial-controls' });
  const previous = element('button', { className: 'testimonial-arrow', text: '‹', attrs: { type: 'button', 'aria-label': 'Previous testimonial' } });
  const next = element('button', { className: 'testimonial-arrow', text: '›', attrs: { type: 'button', 'aria-label': 'Next testimonial' } });
  const dots = element('div', { className: 'testimonial-dots', attrs: { 'aria-label': 'Choose testimonial' } });
  const dotButtons = items.map((_, index) => {
    const dot = element('button', { className: 'testimonial-dot', attrs: { type: 'button', 'aria-label': `Show testimonial ${index + 1}` } });
    dots.append(dot);
    return dot;
  });
  controls.append(previous, dots, next);
  section.append(controls);

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let current = 0;
  let position = 1;
  let timer: number | undefined;
  let pausedByPointer = false;
  let pausedByFocus = false;
  let manualPauseUntil = 0;
  let pointerStart: number | undefined;

  const updateDots = (): void => dotButtons.forEach((dot, index) => dot.setAttribute('aria-current', index === current ? 'true' : 'false'));
  const transform = (animated: boolean): void => {
    track.classList.toggle('without-motion', !animated || reducedMotion.matches);
    track.style.transform = `translateX(${-position * 100}%)`;
    updateDots();
  };
  const clearTimer = (): void => { if (timer !== undefined) window.clearTimeout(timer); timer = undefined; };
  const schedule = (): void => {
    clearTimer();
    if (reducedMotion.matches || pausedByPointer || pausedByFocus || !section.isConnected) return;
    const delay = Math.max(5500, manualPauseUntil - Date.now());
    timer = window.setTimeout(() => move(1, false), delay);
  };
  const settleLoop = (): void => {
    if (position === items.length + 1) position = 1;
    else if (position === 0) position = items.length;
    else return;
    transform(false);
  };
  const move = (direction: 1 | -1, manual = true): void => {
    if (manual) manualPauseUntil = Date.now() + 10000;
    current = nextTestimonialIndex(current, items.length, direction);
    position = reducedMotion.matches ? current + 1 : position + direction;
    transform(true);
    schedule();
  };
  const choose = (index: number): void => {
    manualPauseUntil = Date.now() + 10000;
    current = index;
    position = index + 1;
    transform(true);
    schedule();
  };
  previous.addEventListener('click', () => move(-1));
  next.addEventListener('click', () => move(1));
  dotButtons.forEach((dot, index) => dot.addEventListener('click', () => choose(index)));
  track.addEventListener('transitionend', settleLoop);
  carousel.addEventListener('mouseenter', () => { pausedByPointer = true; clearTimer(); });
  carousel.addEventListener('mouseleave', () => { pausedByPointer = false; schedule(); });
  carousel.addEventListener('focusin', () => { pausedByFocus = true; clearTimer(); });
  carousel.addEventListener('focusout', () => { pausedByFocus = carousel.contains(document.activeElement); if (!pausedByFocus) schedule(); });
  viewport.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowLeft') { event.preventDefault(); move(-1); }
    if (event.key === 'ArrowRight') { event.preventDefault(); move(1); }
  });
  viewport.addEventListener('pointerdown', (event) => { pointerStart = event.clientX; });
  viewport.addEventListener('pointerup', (event) => {
    if (pointerStart === undefined) return;
    const delta = event.clientX - pointerStart;
    pointerStart = undefined;
    if (Math.abs(delta) >= 45) move(delta < 0 ? 1 : -1);
  });
  viewport.addEventListener('pointercancel', () => { pointerStart = undefined; });
  const motionChanged = (): void => { transform(false); schedule(); };
  reducedMotion.addEventListener('change', motionChanged);
  registerCleanup(cleanupRoot ?? section, () => { clearTimer(); reducedMotion.removeEventListener('change', motionChanged); });
  transform(false);
  window.requestAnimationFrame(schedule);
  return section;
}
